import { type ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChartBar, BuildingOffice, Coins, CreditCard, SignOut, Plug, Scales, FileText } from '@phosphor-icons/react';
import clsx from 'clsx';

import { clearCredentials } from '../lib/api';

const NAV_ITEMS = [
  { to: '/', label: 'Home', icon: ChartBar },
  { to: '/organizations', label: 'Organizations', icon: BuildingOffice },
  { to: '/costs', label: 'Costs', icon: Coins },
  { to: '/payments', label: 'Payments', icon: CreditCard },
  { to: '/integrations', label: 'Integrations', icon: Plug },
  { to: '/pricing', label: 'Pricing', icon: Scales },
  { to: '/policies', label: 'Policies', icon: FileText },
];

/**
 * RUTA font wordmark — same lockup as the main webapp nav
 * (`font-black tracking-wider` on Prompt).
 */
function Brand() {
  return (
    <div className="flex items-baseline gap-2.5 shrink-0">
      <span className="text-xl font-black tracking-wider text-white leading-none">
        RUTA
      </span>
      <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-white/45">
        Admin Insights
      </span>
    </div>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  const navigate = useNavigate();

  const logout = () => {
    clearCredentials();
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 bg-forest border-b border-white/10">
        <div className="flex h-14 items-center gap-4 px-3 sm:px-5 lg:px-6">
          <Brand />

          <nav
            className="flex flex-1 items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none"
            aria-label="Main"
          >
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className={({ isActive }) =>
                  clsx(
                    'inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm whitespace-nowrap transition-colors',
                    isActive
                      ? 'bg-white/10 font-medium text-white'
                      : 'text-white/70 hover:bg-white/5 hover:text-white',
                  )
                }
              >
                <Icon className="h-4 w-4 shrink-0 opacity-80" strokeWidth={1.5} />
                {label}
              </NavLink>
            ))}
          </nav>

          <button
            onClick={logout}
            className="shrink-0 inline-flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white"
          >
            <SignOut className="h-4 w-4" strokeWidth={1.5} />
            <span className="hidden sm:inline">Sign out</span>
          </button>
        </div>
      </header>

      <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
    </div>
  );
}
